import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { FiClock, FiUsers, FiX, FiCheck, FiArrowRight, FiZap, FiTrendingUp, FiTarget, FiPhone, FiMail, FiAlertCircle, FiStar, FiBell, FiGift, FiLock } = FiIcons;

function App() {
  const [showPopup, setShowPopup] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Countdown to next offer (30 days from now)
  useEffect(() => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 30);
    targetDate.setHours(22, 0, 0, 0);
    
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate.getTime() - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const missedBenefits = [
    { icon: FiGift, title: "100% Bonus Months", desc: "Double your subscription time for free" },
    { icon: FiZap, title: "AI Automation Suite", desc: "₹6,90,408+ value in premium tools" },
    { icon: FiTrendingUp, title: "Exclusive Insider Pricing", desc: "Never-to-be-repeated pricing" },
    { icon: FiTarget, title: "Priority Support", desc: "Dedicated WhatsApp support channel" }
  ];

  const waitlistBenefits = [
    "🚀 First access to future exclusive offers",
    "💎 VIP pricing for Platinum members only",
    "🔔 Instant notifications for new deals",
    "🎁 Exclusive bonuses for waitlist members",
    "⚡ Skip the queue when offers launch",
    "💰 Better deals than public pricing"
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      role: "Digital Marketing Agency Owner",
      text: "I missed the last offer and regretted it for months. The ROI from Platinum tools is incredible!",
      rating: 5
    },
    {
      name: "Priya Sharma",
      role: "E-commerce Business Owner",
      text: "Being on the waitlist saved me ₹50,000+ when the next offer launched. Never missing again!",
      rating: 5
    },
    {
      name: "Amit Patel",
      role: "SaaS Entrepreneur",
      text: "The exclusive deals for waitlist members are 10x better than regular pricing. Highly recommend!",
      rating: 5
    }
  ];

  const backgroundPattern = "data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 text-white relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 via-purple-600/10 to-blue-600/10"></div>
      <div 
        className="absolute top-0 left-0 w-full h-full opacity-30"
        style={{ backgroundImage: `url("${backgroundPattern}")` }}
      ></div>

      {/* Sticky Top Bar */}
      <div className="sticky top-0 z-50 bg-gradient-to-r from-red-600 via-purple-600 to-blue-600 text-white py-4 px-4 shadow-2xl border-b-4 border-yellow-400">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-sm">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 bg-red-500 px-3 py-1 rounded-full">
                <SafeIcon icon={FiLock} className="text-yellow-300" />
                <span className="font-bold">OFFER CLOSED</span>
              </div>
              <div className="flex items-center gap-2 bg-black bg-opacity-30 px-4 py-2 rounded-lg">
                <SafeIcon icon={FiClock} className="text-yellow-300" />
                <div className="font-mono font-bold text-lg">
                  Next Offer In: <span className="text-green-300">{timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m</span>
                </div>
              </div>
            </div>
            <div className="text-yellow-300 font-bold animate-pulse text-center">
              🔔 JOIN WAITLIST FOR NEXT EXCLUSIVE OFFER!
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section - Offer Closed */}
      <section className="py-20 px-4 relative">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Closed Banner */}
            <div className="inline-block bg-gradient-to-r from-red-600 to-pink-600 text-white px-12 py-6 rounded-2xl text-2xl font-black mb-8 shadow-2xl border-4 border-yellow-400">
              🚫 EXCLUSIVE OFFER HAS ENDED
            </div>

            <h1 className="text-5xl md:text-7xl font-black bg-gradient-to-r from-red-400 via-pink-400 to-purple-400 bg-clip-text text-transparent mb-8 leading-tight">
              You Missed The Biggest Deal of 2025!
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-medium">
              The exclusive month-end Platinum top-up offer has closed. 
              <span className="font-black text-red-400"> All 5 slots were filled in record time.</span>
              <br />
              But don't worry - join our VIP waitlist for the next exclusive offer!
            </p>

            {/* What They Missed */}
            <div className="bg-gradient-to-r from-red-900/50 to-pink-900/50 border-4 border-red-500 rounded-3xl p-8 mb-12 backdrop-blur-lg">
              <h3 className="text-3xl font-black text-red-400 mb-6">What You Missed:</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {missedBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-4 bg-black/30 p-4 rounded-2xl">
                    <div className="bg-red-600 w-12 h-12 rounded-full flex items-center justify-center">
                      <SafeIcon icon={benefit.icon} className="text-white text-xl" />
                    </div>
                    <div className="text-left">
                      <h4 className="font-black text-lg text-white">{benefit.title}</h4>
                      <p className="text-gray-300">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 text-red-300 font-bold text-lg">
                💸 Total Value Lost: ₹6,90,408+ in premium tools and bonuses
              </div>
            </div>

            {/* CTA to Join Waitlist */}
            <motion.button
              onClick={() => setShowPopup(true)}
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block bg-gradient-to-r from-green-500 to-emerald-600 text-white px-16 py-8 rounded-3xl font-black text-3xl shadow-2xl hover:shadow-3xl transition-all duration-300 mb-6 animate-pulse"
            >
              🔔 JOIN VIP WAITLIST NOW!
            </motion.button>

            <p className="text-green-300 font-bold text-xl animate-bounce">
              🎁 Get Exclusive Access to Future Offers!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Waitlist Benefits Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-900/50 to-blue-900/50 backdrop-blur-lg">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-center mb-16 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
              🌟 VIP Waitlist Exclusive Benefits
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {waitlistBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-white/10 to-purple-600/20 backdrop-blur-lg border-2 border-purple-300/30 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 hover:border-yellow-400/50"
                >
                  <p className="text-white font-bold text-lg">{benefit}</p>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <motion.button
                onClick={() => setShowPopup(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 px-12 py-6 rounded-2xl font-black text-2xl shadow-2xl hover:shadow-3xl transition-all duration-300"
              >
                🚀 SECURE MY VIP SPOT NOW!
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-black text-center mb-16 text-white">
            💬 What Our Waitlist Members Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-white/10 to-blue-600/20 backdrop-blur-lg rounded-3xl p-8 border-2 border-blue-300/30 hover:border-yellow-400/50 transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-xl" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <h4 className="font-bold text-white">{testimonial.name}</h4>
                  <p className="text-gray-400 text-sm">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Next Offer Countdown */}
      <section className="py-20 px-4 bg-gradient-to-r from-green-900/50 to-blue-900/50 backdrop-blur-lg">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-12 text-white">
            ⏰ Next Exclusive Offer Launching In:
          </h2>
          
          <div className="grid grid-cols-4 gap-4 mb-12 max-w-2xl mx-auto">
            {[
              { label: 'Days', value: timeLeft.days },
              { label: 'Hours', value: timeLeft.hours },
              { label: 'Minutes', value: timeLeft.minutes },
              { label: 'Seconds', value: timeLeft.seconds }
            ].map((item, index) => (
              <div key={index} className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl p-6 border-2 border-yellow-400">
                <div className="text-4xl font-black text-white mb-2">
                  {String(item.value).padStart(2, '0')}
                </div>
                <div className="text-yellow-300 font-bold text-sm uppercase">
                  {item.label}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-yellow-400/20 to-orange-500/20 border-4 border-yellow-400 rounded-3xl p-8 mb-8 backdrop-blur-lg">
            <p className="text-yellow-300 font-black text-2xl mb-4">
              🎯 Don't Miss Out Again!
            </p>
            <p className="text-white text-lg">
              Join our VIP waitlist and be the first to know when the next exclusive offer launches. 
              <span className="font-black text-green-400"> Waitlist members get better deals and priority access!</span>
            </p>
          </div>

          <motion.button
            onClick={() => setShowPopup(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block bg-gradient-to-r from-red-600 to-pink-600 text-white px-16 py-8 rounded-3xl font-black text-3xl shadow-2xl hover:shadow-3xl transition-all duration-300 animate-pulse"
          >
            🔥 JOIN WAITLIST - DON'T MISS AGAIN!
          </motion.button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Contact Support</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiPhone} />
                  <span>9884899080</span>
                </div>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiMail} />
                  <span>support@yinguru.com</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Legal Links</h3>
              <div className="space-y-2">
                <a href="https://funnelsdone.com/terms" className="block hover:text-blue-400 transition-colors">
                  Terms of Service
                </a>
                <a href="https://funnelsdone.com/privacy" className="block hover:text-blue-400 transition-colors">
                  Privacy Policy
                </a>
                <a href="https://funnelsdone.com/refund-policy" className="block hover:text-blue-400 transition-colors">
                  Refund Policy
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p>© 2025 Funnelsdone LLC and Goguru International Private Limited – All Rights Reserved.</p>
          </div>
        </div>
      </footer>

      {/* Waitlist Popup Modal */}
      <AnimatePresence>
        {showPopup && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setShowPopup(false)}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            >
              {/* Close Button */}
              <button
                onClick={() => setShowPopup(false)}
                className="absolute top-6 right-6 z-10 bg-red-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl hover:bg-red-700 transition-colors"
              >
                <SafeIcon icon={FiX} />
              </button>

              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 text-white p-8 text-center">
                <h3 className="text-3xl md:text-4xl font-black mb-4">
                  🌟 Join VIP Waitlist
                </h3>
                <p className="text-xl font-bold">
                  Get Exclusive Access to Future Premium Offers
                </p>
              </div>

              {/* Form Container */}
              <div className="p-8 max-h-[60vh] overflow-y-auto">
                <iframe
                  src="https://login.aifunnels.app/widget/form/68dc0ad2f3d18"
                  style={{
                    width: '100%',
                    height: '600px',
                    border: 'none',
                    borderRadius: '3px'
                  }}
                  id="inline-68dc0ad2f3d18"
                  data-form-name="Offer Waiting List!"
                  data-layout-iframe-id="inline-68dc0ad2f3d18"
                  data-form-id="68dc0ad2f3d18"
                  data-height="600"
                  title="Offer Waiting List!"
                />
              </div>

              {/* Benefits Footer */}
              <div className="bg-gradient-to-r from-green-100 to-blue-100 p-6 border-t-2 border-gray-200">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
                  <div className="text-gray-800">
                    <div className="text-2xl mb-1">🚀</div>
                    <div className="font-bold text-sm">First Access</div>
                  </div>
                  <div className="text-gray-800">
                    <div className="text-2xl mb-1">💎</div>
                    <div className="font-bold text-sm">VIP Pricing</div>
                  </div>
                  <div className="text-gray-800">
                    <div className="text-2xl mb-1">🎁</div>
                    <div className="font-bold text-sm">Exclusive Bonuses</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
        <motion.button
          onClick={() => setShowPopup(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="block w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-6 font-black text-xl shadow-2xl text-center animate-pulse border-t-4 border-yellow-400"
        >
          🔔 JOIN VIP WAITLIST NOW!
        </motion.button>
      </div>

      {/* Sticky Desktop CTA */}
      <div className="hidden md:block fixed bottom-8 right-8 z-50">
        <motion.button
          onClick={() => setShowPopup(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="block bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-4 rounded-2xl font-black text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 animate-bounce"
        >
          🔔 JOIN WAITLIST!
        </motion.button>
      </div>
    </div>
  );
}

export default App;